(() => {
  const MAX_MESSAGE_LENGTH = 2000;
  const REQUEST_TIMEOUT_MS = 8000;
  const EMAIL = /^[^\s@,<>"']+@[^\s@.,<>"']+(\.[^\s@.,<>"']+)+$/;
  const PHONE = /^\+?[\d\s()-]+$/;

  const createSubmissionId = () => {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0'));
    return [
      hex.slice(0, 4).join(''),
      hex.slice(4, 6).join(''),
      hex.slice(6, 8).join(''),
      hex.slice(8, 10).join(''),
      hex.slice(10, 16).join(''),
    ].join('-');
  };

  const safePagePath = () => {
    const path = window.location.pathname;
    return path.startsWith('/') && !path.startsWith('//') && path.length <= 300
      ? path
      : '';
  };

  const start = (section) => {
    const form = section.querySelector('[data-sixhats-contact-form]');
    if (!form || form.dataset.contactReady === 'true') return;
    form.dataset.contactReady = 'true';

    const findField = (name) => form.querySelector(`[data-contact-field="${name}"]`);
    const findError = (name) => form.querySelector(`[data-contact-error="${name}"]`);
    const submit = form.querySelector('[data-contact-submit]');
    const submitLabel = form.querySelector('[data-contact-submit-label]');
    const status = form.querySelector('[data-contact-status]');
    const message = findField('message');
    const counter = form.querySelector('[data-contact-message-count]');

    const setError = (name, text) => {
      const field = findField(name);
      const error = findError(name);
      if (!field || !error) return;
      field.setAttribute('aria-invalid', text ? 'true' : 'false');
      error.textContent = text;
      error.hidden = !text;
    };

    const clearStatus = () => {
      status.textContent = '';
      status.hidden = true;
    };

    message.addEventListener('input', () => {
      counter.textContent = `${message.value.length} / ${MAX_MESSAGE_LENGTH}`;
      if (message.value.length > 0) setError('message', '');
    });

    for (const name of ['name', 'email', 'phone', 'consent']) {
      findField(name).addEventListener('input', () => setError(name, ''));
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      clearStatus();
      for (const name of ['name', 'email', 'phone', 'message', 'consent']) {
        setError(name, '');
      }

      const values = {
        name: findField('name').value.trim(),
        email: findField('email').value.trim(),
        phone: findField('phone').value.trim(),
        message: message.value.replace(/\r\n?/g, '\n').trim(),
        consent: findField('consent').checked === true,
        website: findField('website').value,
      };
      const digitCount = values.phone.replace(/\D/g, '').length;
      const errors = {
        name:
          values.name.length >= 2 && values.name.length <= 80
            ? ''
            : 'Enter your name.',
        email:
          values.email.length <= 254 && EMAIL.test(values.email)
            ? ''
            : 'Enter a valid email address.',
        phone:
          values.phone.length >= 6 &&
          values.phone.length <= 32 &&
          PHONE.test(values.phone) &&
          values.phone.indexOf('+') <= 0 &&
          digitCount >= 6 &&
          digitCount <= 15
            ? ''
            : 'Enter a valid phone number.',
        message:
          values.message.length > 0 && values.message.length <= MAX_MESSAGE_LENGTH
            ? ''
            : 'Enter a message up to 2,000 characters.',
        consent: values.consent ? '' : 'Please agree so we can respond.',
      };

      const firstInvalid = Object.keys(errors).find((name) => errors[name]);
      for (const [name, text] of Object.entries(errors)) setError(name, text);
      if (firstInvalid) {
        findField(firstInvalid).focus();
        return;
      }

      submit.disabled = true;
      submitLabel.textContent = 'Sending…';
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      try {
        const response = await fetch(section.dataset.leadEndpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            submissionId: createSubmissionId(),
            name: values.name,
            email: values.email,
            phone: values.phone,
            consent: true,
            source: 'contact-page',
            pagePath: safePagePath(),
            website: values.website,
            message: values.message,
          }),
          signal: controller.signal,
        });
        const payload = await response.json().catch(() => null);
        if (
          response.ok &&
          payload &&
          payload.ok === true &&
          payload.customerSynced === true
        ) {
          const success = section.querySelector('[data-contact-success]');
          form.reset();
          form.hidden = true;
          success.hidden = false;
          success.focus();
          document.dispatchEvent(
            new CustomEvent('sixhats:lead-submitted', {
              detail: { source: 'contact-page' },
            }),
          );
          return;
        }
        status.textContent =
          response.status === 429
            ? 'Please wait a minute, then try again.'
            : 'We couldn’t send your message. Please try again.';
        status.hidden = false;
      } catch (_error) {
        status.textContent = 'We couldn’t send your message. Please try again.';
        status.hidden = false;
      } finally {
        window.clearTimeout(timeout);
        submit.disabled = false;
        submitLabel.textContent = 'Send message';
      }
    });
  };

  const mount = (scope = document) => {
    scope.querySelectorAll('[data-sixhats-contact-section]').forEach(start);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  } else {
    mount();
  }
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
})();
